import { useState } from 'react';
import i18next from 'i18next';
import BaseButton from 'components/atoms/baseButton';
import { makeid } from 'utils/helpers/makeid';
import BaseModal from 'components/atoms/base-modal';

import s from './styles.module.scss';

import type { ReactNode } from 'react';

interface IAlertModalProps {
  open: boolean;
  title: string | ReactNode;
  message?: string | ReactNode;
  type: btnType;
  handleOk: () => Promise<void>;
  handleCancel: (res?: boolean) => void;
  confirmLoading?: boolean;
  successBtnLabel?: string;
  rejectBtnLabel?: string;
  showReject?: boolean;
  messageTitle?: string | ReactNode;
  customMessage?: string | ReactNode;
  zIndex?: number;
}

type btnType = 'primary' | 'danger';

const AlertModal = ({
  open,
  title,
  type,
  message,
  successBtnLabel,
  rejectBtnLabel,
  handleOk,
  handleCancel,
  confirmLoading,
  messageTitle,
  customMessage,
  zIndex = 1070,
  showReject = true,
}: IAlertModalProps) => {
  return (
    <BaseModal
      title={title}
      open={open}
      onCancel={() => {
        if (confirmLoading) return;
        handleCancel();
      }}
      withFooter={[
        showReject && (
          <BaseButton
            key="form-cancel-button"
            type="secondary"
            label={rejectBtnLabel ?? i18next.t('controls.cancel')}
            onClick={() => handleCancel(false)}
            disabled={confirmLoading}
            dataCy="form-cancel-button"
          />
        ),
        <BaseButton
          key="form-delete-button"
          type="primary"
          label={
            successBtnLabel ??
            (type === 'danger'
              ? i18next.t('controls.delete')
              : i18next.t('controls.ok'))
          }
          onClick={handleOk}
          loading={confirmLoading}
          dataCy="form-delete-button"
        />,
      ].filter(Boolean)}
      zIndex={zIndex}
    >
      <div>
        <p className={s['alert-message']}>
          {customMessage ?? (
            <>
              {messageTitle && (
                <p
                  className={s['alert-title']}
                  style={{ paddingBottom: message ? 10 : 0 }}
                >
                  {messageTitle}
                </p>
              )}
              <p>{message}</p>
            </>
          )}
        </p>
      </div>
    </BaseModal>
  );
};

export interface IAlertOptions {
  title: string | ReactNode;
  message?: string | ReactNode;
  type?: 'primary' | 'danger';
  successBtnLabel?: string;
  rejectBtnLabel?: string;
  showReject?: boolean;
  customMessage?: string | ReactNode;
  messageTitle?: string | ReactNode;
  zIndex?: number;
  handleOk?: () => void | Promise<void>;
  handleCancel?: (res: boolean | undefined) => void;
  key?: string;
}

const useAlert = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<IAlertOptions>({
    title: '',
    message: '',
    type: 'primary',
    key: makeid(10),
  });
  const [popupController, setPopupController] = useState<{
    resolve: any;
    reject: any;
  }>({ resolve: null, reject: null });
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);

  const showAlert = async (optionsProps: IAlertOptions): Promise<boolean> => {
    setOptions(optionsProps);
    setOpen(true);
    setConfirmLoading(false);
    const popupPromise: Promise<boolean> = new Promise((ok, fail) => {
      setPopupController({
        resolve: ok,
        reject: fail,
      });
    });

    return popupPromise;
  };
  const handleOk = async () => {
    if (confirmLoading) return;

    try {
      setConfirmLoading(true);
      if (options.handleOk) {
        await options.handleOk();
      }

      popupController.resolve(true);
      setOpen(false);
    } catch (error) {
      // Keep modal open so user can retry/cancel.
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleCancel = (res: boolean | undefined) => {
    if (confirmLoading) return;
    popupController.resolve(res);
    setOpen(false);
  };

  return {
    showAlert,
    Alert: (
      <AlertModal
        open={open}
        type={options.type ?? 'primary'}
        title={options.title}
        message={options?.message}
        handleOk={handleOk}
        handleCancel={(res) => {
          handleCancel(res);
          if (options.handleCancel) options.handleCancel(res);
        }}
        confirmLoading={confirmLoading}
        successBtnLabel={options?.successBtnLabel}
        rejectBtnLabel={options?.rejectBtnLabel}
        messageTitle={options?.messageTitle}
        customMessage={options?.customMessage}
        zIndex={options?.zIndex}
        showReject={options?.showReject}
        key={options?.key}
      />
    ),
  };
};

export default useAlert;
